import { ClientEntityType } from "../supabase/supabaseTypes";

/**
 * Get the display name for a client (without title)
 * Handles company clients, person clients, and edge cases
 */
export function getClientDisplayName(client: ClientEntityType | null | undefined, lastNameFirst?: boolean): string {
	if (!client) return "";

	// Company clients: use company name
	if (client.is_company && client.company_name) {
		return client.company_name;
	}

	// Person clients: use first and last name
	const firstName = client.first_name?.trim() || "";
	const lastName = client.last_name?.trim() || "";
	let fullName: string;
	if (lastNameFirst) {
		if (firstName && lastName) {
			fullName = `${lastName}, ${firstName}`;
		} else {
			fullName = lastName || firstName;
		}
	} else {
		if (firstName && lastName) {
		fullName = `${firstName} ${lastName}`.trim();
		} else {
			fullName = firstName || lastName;
		}
	}

	// Fallback to company name if no person name is available
	if (!fullName && client.company_name) {
		return client.company_name;
	}

	return fullName;
}

/**
 * Format client name for sidebar display
 */
export function formatClientNameForSidebar(
	firstName: string | null,
	lastName: string | null,
	companyName?: string | null,
	isCompany?: boolean | null
): string {
	if (isCompany && companyName) {
		return companyName;
	}

	const trimmedFirstName = firstName?.trim() || "";
	const trimmedLastName = lastName?.trim() || "";

	if (trimmedFirstName && trimmedLastName) {
		// Last name + first name initial
		return `${trimmedLastName} ${trimmedFirstName.charAt(0)}.`;
	} else if (trimmedFirstName) {
		return trimmedFirstName;
	} else if (trimmedLastName) {
		return trimmedLastName;
	} else if (companyName) {
		// Fallback to company name
		return companyName;
	}
	return "";
}

/**
 * Get client initials that fit well in the avatar
 */
export function getClientInitials(
	firstName: string | null,
	lastName: string | null,
	companyName?: string | null,
	isCompany?: boolean | null
): string {
	if (isCompany && companyName && companyName.trim().length > 0) {
		if (companyName.trim().length === 1) {
			return companyName.charAt(0).toUpperCase();
		}
		return companyName.substring(0, 2).toUpperCase();
	}

	if (
		firstName &&
		firstName.trim().length > 0 &&
		lastName &&
		lastName.trim().length > 0
	) {
		// Return first letter of first + first letter of last name
		return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
	} else if (firstName && firstName.trim().length > 0) {
		return firstName.charAt(0).toUpperCase();
	} else if (lastName && lastName.trim().length > 0) {
		return lastName.charAt(0).toUpperCase();
	} else if (companyName && companyName.trim().length > 0) {
		// Fallback to company name initials
		if (companyName.trim().length === 1) {
			return companyName.charAt(0).toUpperCase();
		}
		return companyName.substring(0, 2).toUpperCase();
	}
	return "?";
}

/**
 * Get the full name for a client including title
 * Handles company clients, person clients, and edge cases
 */
export function getClientFullName(client: ClientEntityType | null | undefined): string {
	if (!client) return "";

	// Company clients: use company name (no title)
	if (client.is_company && client.company_name) {
		return client.company_name;
	}

	// Person clients: include title if available
	const parts = [
		client.title,
		client.first_name,
		client.last_name
	].filter(Boolean);

	const fullName = parts.join(" ");

	// Fallback to company name if no person name is available
	if (!fullName && client.company_name) {
		return client.company_name;
	}

	return fullName;
}

/**
 * Get client name with code in parentheses
 * e.g., "John Doe (JD01)" or "Company Name (CO01)"
 */
export function getClientNameWithCode(client: ClientEntityType | null | undefined): string {
	if (!client) return "";

	const name = getClientDisplayName(client);
	if (client.code) {
		return `${name} (${client.code})`;
	}
	return name;
}

/**
 * Filter out inactive clients from an array
 */
export function filterActiveClients(clients: ClientEntityType[]): ClientEntityType[] {
	return clients.filter((client) => !client?.is_inactive);
}

/**
 * Get the name to use for sorting (company name for companies, last name for persons)
 */
export function getClientSortName(client: ClientEntityType): string | null {
	if (client.is_company && client.company_name) {
		return client.company_name;
	}
	return client.last_name;
}

/**
 * Sort clients with favorites first, then by code or last name
 */
export function sortClients(clients: ClientEntityType[], sortByCode: boolean): ClientEntityType[] {
	return [...clients].sort((a, b) => {
		// Favorite clients first
		if (a.favorite && b.favorite) {
			const aCode = a.code ?? "";
			const bCode = b.code ?? "";

			// If both are favorites, sort by the configured method
			if (sortByCode) {
				return aCode.toLowerCase().localeCompare(bCode.toLowerCase());
			} else {
				// Sort by name (company name for companies, last name for persons)
				const aName = getClientSortName(a);
				const bName = getClientSortName(b);
				if (!aName || !bName) return 0;
				return aName.toLowerCase().localeCompare(bName.toLowerCase());
			}
		} else if (a.favorite) {
			return -1;
		} else if (b.favorite) {
			return 1;
		}

		// Then sort non-favorites by the configured method
		if (sortByCode) {
			const aCode = a.code ?? "";
			const bCode = b.code ?? "";
			return aCode.toLowerCase().localeCompare(bCode.toLowerCase());
		} else {
			// Sort by name (company name for companies, last name for persons)
			const aName = getClientSortName(a);
			const bName = getClientSortName(b);
			if (!aName) return -1;
			if (!bName) return 1;
			return aName.toLowerCase().localeCompare(bName.toLowerCase());
		}
	});
}

export function getProfileDisplayName(
	profile: { first_name?: string | null; last_name?: string | null; email?: string | null } | null | undefined,
	fallback: string = ""
): string {
	if (!profile) return fallback;
	const name = `${profile.first_name?.trim() || ""} ${profile.last_name?.trim() || ""}`.trim();
	return name || profile.email || fallback;
}

export function getPatientDisplayName(
	patient: { first_name?: string | null; last_name?: string | null } | null | undefined,
	lastNameFirst?: boolean
): string {
	if (!patient) return "";
	const firstName = patient.first_name?.trim() || "";
	const lastName = patient.last_name?.trim() || "";
	if (firstName && lastName) {
		return lastNameFirst ? `${lastName}, ${firstName}` : `${firstName} ${lastName}`;
	}
	return lastName || firstName;
}
